/**
 * measureStore — AK-320 «Линейка на карте» v1.
 *
 * ОТДЕЛЬНЫЙ лёгкий zustand-стор, НЕ часть mapReducer/useMapStoreV2.
 * Архитектор (соблюдать строго): measure-состояние изолировано от карты,
 * чтобы не раздувать reducer и не задевать collision/pills-инварианты
 * (#359). Стор хранит только UI-уровень: активен ли тул, какой режим,
 * и последний посчитанный результат для панели.
 *
 * Геометрия точек / машина состояний рисования живёт в MeasureController
 * (imperative, привязан к MapLibre) — сюда стекает только готовый результат.
 */
import { create } from 'zustand';

/** 4 режима measure (founder scope v1, БЕЗ snap). */
export type MeasureMode = 'line' | 'path' | 'area' | 'circle';

/**
 * Способ построения круга (AK-320 v1 founder UAT):
 *   - 'center'   — клик центр → клик край (исходное поведение)
 *   - 'diameter' — клик край → клик противоположный край; центр = середина,
 *     радиус = половина расстояния
 */
export type CircleMode = 'center' | 'diameter';

/** Результат измерения для панели (готовые форматированные строки + сырьё). */
export interface MeasureResult {
  mode: MeasureMode;
  /** Основная строка результата (расстояние или площадь — га крупно). */
  primary: string;
  /** Вторичная строка (периметр / радиус / контекст-единица), опц. */
  secondary?: string;
  /** Сырьё для возможного экспорта/отладки. */
  raw: {
    distanceMeters?: number;
    areaSqm?: number;
    perimeterMeters?: number;
    radiusMeters?: number;
    /** Длины сегментов ломаной, метры. */
    segmentsMeters?: number[];
  };
}

interface MeasureStore {
  /** Тул активен (crosshair-курсор, слушатели навешены). */
  active: boolean;
  /** Текущий режим. */
  mode: MeasureMode;
  /** Способ построения круга (только для mode='circle'). */
  circleMode: CircleMode;
  /** Последний результат (null пока ничего не нарисовано). */
  result: MeasureResult | null;
  /**
   * Монотонный счётчик «Очистить». Меняется → контроллер по подписке стирает
   * текущий рисунок, оставаясь активным. Нужен отдельный сигнал, т.к. повтор
   * того же mode НЕ триггерит setMode в подписке (root-cause бага «Очистить»).
   */
  clearNonce: number;
  /** Монотонный счётчик «Готово» — финализирует текущее рисование. */
  finalizeNonce: number;

  /** Включить тул в заданном режиме (по умолчанию 'line'). */
  activate: (mode?: MeasureMode) => void;
  /** Полностью выключить тул (оверлей чистит контроллер по подписке). */
  deactivate: () => void;
  /** Сменить режим (контроллер при этом очищает текущий рисунок). */
  setMode: (mode: MeasureMode) => void;
  /** Переключить способ построения круга (контроллер очищает рисунок). */
  setCircleMode: (circleMode: CircleMode) => void;
  /** Записать готовый результат (вызывает контроллер). */
  setResult: (result: MeasureResult | null) => void;
  /** Очистить текущий рисунок, оставаясь активным («Очистить»). */
  clear: () => void;
  /** Финализировать текущее рисование («Готово»). */
  finalize: () => void;
}

export const useMeasureStore = create<MeasureStore>((set) => ({
  active: false,
  mode: 'line',
  circleMode: 'center',
  result: null,
  clearNonce: 0,
  finalizeNonce: 0,

  activate: (mode = 'line') => set({ active: true, mode, result: null }),
  deactivate: () => set({ active: false, result: null }),
  setMode: (mode) => set({ mode, result: null }),
  setCircleMode: (circleMode) => set({ circleMode, result: null }),
  setResult: (result) => set({ result }),
  clear: () => set((s) => ({ result: null, clearNonce: s.clearNonce + 1 })),
  finalize: () => set((s) => ({ finalizeNonce: s.finalizeNonce + 1 })),
}));
